import AlertCircleIcon from '@assets/icons/alert.svg';
import i18n from '@/utils/i18n';
import { ActivityIndicator } from '@/components/activity-indicator';
import { CONTENT_HEIGHT } from '@/components/navigation-header-content';
import { createStyles } from '@/contexts/theme-context';
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { generateAttachmentList } from '@/components/attachment-list/utils';
import {
  getAttachmentFileName,
  getFilenameFromUrl,
  PROCESSING_STATUSES,
} from '@/screens/order-inbounds/utils';
import {
  type ImageMapArea,
  type InboundAttachment,
  type InboundAudioAttachment,
  InboundEmlAttachment,
  type InboundHTMLAttachment,
  type InboundInlineImageAttachment,
} from '@/components/attachment-list/attachment-list-types';
import { type NavigationState } from 'react-native-tab-view';
import { OrderInboundsDetailEmailThreadContent } from './order-inbounds-detail-email-thread-content';
import { OrderInboundsDetailGroupedHtmlContent } from './order-inbounds-detail-grouped-html-content';
import { OrderInboundsDetailGroupedImagesContent } from './order-inbounds-detail-grouped-images-content';
import { OrderInboundsDetailTabContent } from './order-inbounds-detail-tab-content';
import { OrderInboundsDetailTabsLabel } from './order-inbounds-detail-tabs-label';
import { OrderInboundsDetailTabView } from './order-inbounds-detail-tabs-view';
import { OrderInboundsDetailVoicemailContent } from './order-inbounds-detail-voicemail-content';
import { type OrderMetadata } from '@/screens/order-inbounds/types';
import { OriginalInboundAudio } from '@/screens/order-inbounds/original-inbound-audio';
import { OriginalInboundMessage } from '@/screens/order-inbounds/original-inbound-message';
import { OriginalInboundText } from '@/screens/order-inbounds/original-inbound-text';
import { Platform, ScrollView, StyleProp, View, ViewStyle } from 'react-native';
import { Toast, Type } from '@/components/toast';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { useThreadOrderEventsContext } from '@/screens/order-inbounds/thread-order-events-context';
import type {
  OnAttachmentListItemPressCallback,
  OrderInboundsDetailTabsRoute,
} from './types';

interface Props {
  containerStyle?: StyleProp<ViewStyle>;
  isSummaryView?: boolean;
  onAreaSelect?: (area: ImageMapArea) => void;
  onHighlightElementSelect?: (searchInputUUID: string) => void;
  orderMetadata: OrderMetadata[];
  selectedAreaID?: string;
  showTranslations?: boolean;
  translationToggle?: React.ReactNode;
}

export const OrderInboundsDetailTabs: FC<Props> = ({
  containerStyle,
  isSummaryView,
  onAreaSelect,
  onHighlightElementSelect,
  orderMetadata,
  selectedAreaID,
  showTranslations,
  translationToggle,
}) => {
  const [styles, theme] = useStyles();
  const isEmailThreadsEnabled = useSupplierDynamicConfigFlag(
    'smart_order_config',
    'email_threads_enabled',
  );

  const { onThreadOrderProcessed, onThreadOrderProcessing } =
    useThreadOrderEventsContext();

  const [tabIndex, setTabIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [hasEmlErrorByUrl, setHasEmlErrorByUrl] = useState<
    Record<string, boolean>
  >({});
  const [hasHtmlWithNoContentByUrl, setHasHtmlWithNoContentByUrl] = useState<
    Record<string, boolean>
  >({});
  const [clickedInlineImages, setClickedInlineImages] = useState<
    Map<string, number>
  >(new Map());
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null,
  );
  const [pendingReplyThreadId, setPendingReplyThreadId] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!isEmailThreadsEnabled) {
      return;
    }
    return onThreadOrderProcessing((event) => {
      if (
        orderMetadata.some((o) => o.threadId && o.threadId === event.threadId)
      ) {
        setPendingReplyThreadId(event.threadId);
      }
    });
  }, [onThreadOrderProcessing, isEmailThreadsEnabled, orderMetadata]);

  useEffect(() => {
    if (!isEmailThreadsEnabled) {
      return;
    }
    return onThreadOrderProcessed((event) => {
      if (
        orderMetadata.some((o) => o.threadId && o.threadId === event.threadId)
      ) {
        setPendingReplyThreadId(null);
      }
    });
  }, [onThreadOrderProcessed, isEmailThreadsEnabled, orderMetadata]);

  // When navigating fresh to a thread where a reply was already processing on mount,
  // the onThreadOrderProcessing event was missed. Set pendingReplyThreadId from the
  // initial orderMetadata so the toast appears immediately on entry.
  const hasInitializedPendingReplyRef = useRef(false);
  useEffect(() => {
    if (
      !isEmailThreadsEnabled ||
      hasInitializedPendingReplyRef.current ||
      orderMetadata.length < 2
    ) {
      return;
    }
    hasInitializedPendingReplyRef.current = true;
    const processingOrder = orderMetadata.find(
      (o) => o.threadId && PROCESSING_STATUSES.has(o.processingStatus),
    );
    if (processingOrder?.threadId) {
      setPendingReplyThreadId(processingOrder.threadId);
    }
  }, [isEmailThreadsEnabled, orderMetadata]);

  const { top } = useSafeAreaInsets();
  const { isSmallLayout, isSmallTabletLayout } = useLayoutContext();
  const headerOffset =
    isSmallLayout && Platform.OS !== 'web' ? top + CONTENT_HEIGHT + 8 : 0;

  const handleEmlError = useCallback((attachmentUrl: string) => {
    setHasEmlErrorByUrl((prev) => ({
      ...prev,
      [attachmentUrl]: true,
    }));
  }, []);

  const handleHtmlHasNoContent = useCallback((attachmentUrl: string) => {
    setHasHtmlWithNoContentByUrl((prev) => ({
      ...prev,
      [attachmentUrl]: true,
    }));
  }, []);

  const shouldShowOriginalInboundText = useCallback(
    (order: OrderMetadata, attachmentUrl: string): boolean => {
      if (!order.searchText) {
        return false;
      }

      const hasEmlAttachment =
        order.attachments?.some((attachment) => attachment.type === 'EML') ??
        false;

      return (
        !hasEmlAttachment ||
        hasEmlErrorByUrl[attachmentUrl] ||
        hasHtmlWithNoContentByUrl[attachmentUrl]
      );
    },
    [hasEmlErrorByUrl, hasHtmlWithNoContentByUrl],
  );

  const navigationRoutes: NavigationState<OrderInboundsDetailTabsRoute>['routes'] =
    useMemo(() => {
      const locale = i18n.locale;
      const routes: NavigationState<OrderInboundsDetailTabsRoute>['routes'] =
        [];

      // Collects per-thread data: email items for the EMAIL_THREAD tab, and
      // attachment routes for all orders in the thread (including orders with
      // no EML attachment).
      const emailThreadData = new Map<
        string,
        {
          attachmentRoutes: OrderInboundsDetailTabsRoute[];
          emailItems: {
            attachment: InboundEmlAttachment;
            orderMetadata: OrderMetadata;
          }[];
        }
      >();

      const getOrCreateThreadData = (threadId: string) => {
        if (!emailThreadData.has(threadId)) {
          emailThreadData.set(threadId, {
            attachmentRoutes: [],
            emailItems: [],
          });
        }
        return emailThreadData.get(threadId)!;
      };

      for (const order of orderMetadata) {
        let attachments: InboundAttachment[] = order.attachments ?? [];
        const isEmailThreadOrder =
          isEmailThreadsEnabled && order.source === 'EMAIL';

        switch (order.source) {
          case 'EMAIL': {
            const { emlAttachment, emlTranslations, filteredAttachments } =
              generateAttachmentList(order.attachments ?? []);

            if (emlAttachment) {
              const attachmentToUse =
                showTranslations && emlTranslations[locale]
                  ? emlTranslations[locale]
                  : emlAttachment;

              if (isEmailThreadOrder && order.threadId) {
                getOrCreateThreadData(order.threadId).emailItems.push({
                  attachment: attachmentToUse,
                  orderMetadata: order,
                });
              } else {
                routes.push({
                  attachment: attachmentToUse,
                  key: attachmentToUse.url,
                  orderMetadata: order,
                  title: i18n.t('email'),
                  type: 'ATTACHMENT',
                });
              }
            }

            // this contains all of the attachments without the EML attachments
            attachments = filteredAttachments;
            break;
          }
          case 'VOICEMAIL':
            routes.push({
              key:
                order.voicemailRecordingUrl ??
                `${order.smartOrderUUID}-voicemail`,
              orderMetadata: order,
              title: i18n.t('voicemail'),
              type: 'VOICEMAIL',
            });
            break;
          case 'SMS':
            routes.push({
              key: `${order.smartOrderUUID}-message`,
              orderMetadata: order,
              title: i18n.t('sms'),
              type: 'MESSAGE',
            });
            break;
          default:
            break;
        }

        // we always want to put the attachments after the main message (Email, Voicemail, SMS, etc.)
        // For non-VOICEMAIL sources, create separate tabs for audio attachments
        if (order.source !== 'VOICEMAIL') {
          const audioAttachments = attachments.filter(
            (att): att is InboundAudioAttachment => att.type === 'AUDIO',
          );

          for (const audioAttachment of audioAttachments) {
            const audioRoute: OrderInboundsDetailTabsRoute = {
              attachmentIndex: audioAttachment.attachmentIndex,
              audioUrl: audioAttachment.url,
              key: `${order.smartOrderUUID}-audio-${audioAttachment.attachmentIndex}`,
              orderMetadata: order,
              title: getFilenameFromUrl(audioAttachment.url),
              type: 'AUDIO_ATTACHMENT',
            };

            if (isEmailThreadOrder && order.threadId) {
              getOrCreateThreadData(order.threadId).attachmentRoutes.push(
                audioRoute,
              );
            } else {
              routes.push(audioRoute);
            }
          }
        }

        const htmlAttachments: InboundHTMLAttachment[] = [];
        const nonHtmlNonAudioAttachments: InboundAttachment[] = [];

        for (const att of attachments) {
          if (att.type === 'AUDIO') {
            continue;
          }

          if (att.type === 'HTML') {
            htmlAttachments.push(att);
          } else {
            nonHtmlNonAudioAttachments.push(att);
          }
        }

        const uniqueInlineAttachmentUrls = new Set<string>();

        for (const attachment of nonHtmlNonAudioAttachments) {
          if (attachment.type === 'INLINE_IMAGE') {
            uniqueInlineAttachmentUrls.add(attachment.url);
          }
        }

        const uniqueAttachmentNamesMap = new Map<string, InboundAttachment[]>();

        for (const attachment of nonHtmlNonAudioAttachments) {
          if (attachment.type === 'INLINE_IMAGE') {
            continue;
          }

          if (
            attachment.type === 'IMAGE_MAP' &&
            uniqueInlineAttachmentUrls.has(attachment.url)
          ) {
            continue;
          }

          const fileName = getAttachmentFileName(
            attachment.url,
            order.smartOrderUUID,
          );
          if (fileName === null) {
            continue;
          }

          uniqueAttachmentNamesMap.set(fileName, [
            ...(uniqueAttachmentNamesMap.get(fileName) ?? []),
            attachment,
          ]);
        }

        for (const [fileName, groupedAttachments] of uniqueAttachmentNamesMap) {
          const title = groupedAttachments[0].originalFilename ?? fileName;

          if (groupedAttachments.length > 1) {
            const groupedRoute: OrderInboundsDetailTabsRoute = {
              images: groupedAttachments
                .filter((attachment) => attachment.type === 'IMAGE_MAP')
                .map((image) => ({ areas: image.areas, url: image.url })),
              key: `${order.smartOrderUUID}-${title}`,
              title: title,
              type: 'GROUPED_IMAGES',
            };

            if (isEmailThreadOrder && order.threadId) {
              getOrCreateThreadData(order.threadId).attachmentRoutes.push(
                groupedRoute,
              );
            } else {
              routes.push(groupedRoute);
            }
          } else if (groupedAttachments.length === 1) {
            const attachmentRoute: OrderInboundsDetailTabsRoute = {
              attachment: groupedAttachments[0],
              key: `${order.smartOrderUUID}-${title}`,
              orderMetadata: order,
              title: title,
              type: 'ATTACHMENT',
            };

            if (isEmailThreadOrder && order.threadId) {
              getOrCreateThreadData(order.threadId).attachmentRoutes.push(
                attachmentRoute,
              );
            } else {
              routes.push(attachmentRoute);
            }
          }
        }

        const uniqueHtmlNamesMap = new Map<string, InboundHTMLAttachment[]>();

        for (const attachment of htmlAttachments) {
          let key: string;

          if (attachment.originalFilename) {
            key = attachment.originalFilename;
          } else {
            key =
              getAttachmentFileName(attachment.url, order.smartOrderUUID) ??
              attachment.url;
          }

          uniqueHtmlNamesMap.set(key, [
            ...(uniqueHtmlNamesMap.get(key) ?? []),
            attachment,
          ]);
        }

        for (const [key, groupedHtml] of uniqueHtmlNamesMap) {
          const title = groupedHtml[0].originalFilename ?? key;

          if (groupedHtml.length > 1) {
            const groupedHtmlRoute: OrderInboundsDetailTabsRoute = {
              attachments: groupedHtml,
              key: `${order.smartOrderUUID}-${title}`,
              orderMetadata: order,
              title,
              type: 'GROUPED_HTML',
            };

            if (isEmailThreadOrder && order.threadId) {
              getOrCreateThreadData(order.threadId).attachmentRoutes.push(
                groupedHtmlRoute,
              );
            } else {
              routes.push(groupedHtmlRoute);
            }
          } else {
            const attachmentRoute: OrderInboundsDetailTabsRoute = {
              attachment: groupedHtml[0],
              key: `${order.smartOrderUUID}-${title}`,
              orderMetadata: order,
              title,
              type: 'ATTACHMENT',
            };

            if (isEmailThreadOrder && order.threadId) {
              getOrCreateThreadData(order.threadId).attachmentRoutes.push(
                attachmentRoute,
              );
            } else {
              routes.push(attachmentRoute);
            }
          }
        }
      }

      if (isEmailThreadsEnabled) {
        for (const [
          threadId,
          { emailItems, attachmentRoutes },
        ] of emailThreadData) {
          if (emailItems.length > 0) {
            routes.push({
              items: emailItems,
              key: threadId,
              title: i18n.t('email'),
              type: 'EMAIL_THREAD',
            });
          }
          routes.push(...attachmentRoutes);
        }
      }

      const clickedInlineImagesIdxSorted = Array.from(
        clickedInlineImages.entries(),
      ).sort((a, b) => a[1] - b[1]);

      for (const [
        inboundContentId,
        tabNumber,
      ] of clickedInlineImagesIdxSorted) {
        let inlineImage: InboundInlineImageAttachment | undefined;
        let imageOrder: OrderMetadata | undefined;

        for (const order of orderMetadata) {
          const found = order.attachments?.find(
            (att) =>
              att.type === 'INLINE_IMAGE' &&
              att.inboundContentId === inboundContentId,
          );
          if (found?.type === 'INLINE_IMAGE') {
            inlineImage = found;
            imageOrder = order;
            break;
          }
        }

        if (!inlineImage || !imageOrder) {
          continue;
        }

        routes.push({
          attachment: inlineImage,
          key: `inline-image-${inboundContentId}`,
          orderMetadata: imageOrder,
          title: `Image ${tabNumber}`,
          type: 'ATTACHMENT',
        });
      }

      return routes;
    }, [
      orderMetadata,
      showTranslations,
      clickedInlineImages,
      isEmailThreadsEnabled,
    ]);

  const handleInlineImageClick = useCallback(
    (inboundContentId: string, index: number) => {
      if (clickedInlineImages.has(inboundContentId)) {
        const tabIndex = navigationRoutes.findIndex(
          (route) =>
            route.type === 'ATTACHMENT' &&
            route.attachment.type === 'INLINE_IMAGE' &&
            route.attachment.inboundContentId === inboundContentId,
        );

        if (tabIndex !== -1) {
          setTabIndex(tabIndex);
        }
        return;
      }

      setClickedInlineImages((prev) => {
        const newMap = new Map(prev);
        newMap.set(inboundContentId, index + 1);
        return newMap;
      });

      setPendingNavigation(inboundContentId);
    },
    [navigationRoutes, clickedInlineImages],
  );

  const onEmlAttachmentListItemPress =
    useCallback<OnAttachmentListItemPressCallback>(
      (attachment) => {
        let tabIdx: number | null = null;

        for (let i = 0; i < navigationRoutes.length; i++) {
          const route = navigationRoutes[i];

          switch (route.type) {
            case 'ATTACHMENT':
              if (route.attachment.url === attachment.url) {
                tabIdx = i;
              }
              break;
            case 'GROUPED_IMAGES':
              const imageIdx = route.images.find(
                (image) => image.url === attachment.url,
              );

              if (Boolean(imageIdx)) {
                tabIdx = i;
              }
              break;
            case 'GROUPED_HTML':
              if (route.attachments.some((a) => a.url === attachment.url)) {
                tabIdx = i;
              }
              break;
            case 'AUDIO_ATTACHMENT':
              if (route.audioUrl === attachment.url) {
                tabIdx = i;
              }
              break;
            default:
              break;
          }
        }

        if (tabIdx !== null) {
          setTabIndex(tabIdx);
        }
      },
      [navigationRoutes],
    );

  useEffect(() => {
    if (!pendingNavigation) {
      return;
    }

    const tabIndex = navigationRoutes.findIndex(
      (route) =>
        route.type === 'ATTACHMENT' &&
        route.attachment.type === 'INLINE_IMAGE' &&
        route.attachment.inboundContentId === pendingNavigation,
    );

    if (tabIndex !== -1) {
      setTabIndex(tabIndex);
      setPendingNavigation(null);
    }
  }, [pendingNavigation, navigationRoutes]);

  const navigationState: NavigationState<OrderInboundsDetailTabsRoute> =
    useMemo(
      () => ({
        index: tabIndex,
        routes: navigationRoutes,
      }),
      [tabIndex, navigationRoutes],
    );

  if (navigationRoutes.length === 0) {
    return null;
  }

  return (
    <View
      onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
      style={[styles.container, { paddingTop: headerOffset }]}
    >
      <OrderInboundsDetailTabView
        indicatorStyle={styles.tabBarIndicatorStyle}
        label={(props) => <OrderInboundsDetailTabsLabel {...props} />}
        navigationState={navigationState}
        onIndexChange={setTabIndex}
        renderScene={(props) => {
          if (containerWidth === null) {
            return (
              <View style={styles.loadingContainer}>
                <ActivityIndicator />
              </View>
            );
          }

          switch (props.route.type) {
            case 'ATTACHMENT': {
              const attachment = props.route.attachment;
              const order = props.route.orderMetadata;

              if (
                attachment.type === 'EML' &&
                shouldShowOriginalInboundText(order, attachment.url)
              ) {
                return (
                  <ScrollView
                    contentContainerStyle={styles.tabContentContainer}
                  >
                    {translationToggle}
                    <OriginalInboundText
                      onSelect={onHighlightElementSelect}
                      order={order}
                      selectedInputUUID={selectedAreaID}
                      showTranslation={showTranslations}
                    />
                  </ScrollView>
                );
              }

              const handleEmlErrorForAttachment = (
                emlAttachment: InboundAttachment,
              ) => {
                handleEmlError(emlAttachment.url);
              };

              const handleHtmlHasNoContentForAttachment = (
                emlAttachment: InboundAttachment,
              ) => {
                handleHtmlHasNoContent(emlAttachment.url);
              };

              return (
                <OrderInboundsDetailTabContent
                  attachment={attachment}
                  containerWidth={containerWidth}
                  onAreaSelect={(input) => onAreaSelect?.(input)}
                  onEMLAttachmentListItemPress={onEmlAttachmentListItemPress}
                  onEmlError={handleEmlErrorForAttachment}
                  onHighlightElementSelect={(input) =>
                    onHighlightElementSelect?.(input)
                  }
                  onHtmlHasNoContent={handleHtmlHasNoContentForAttachment}
                  onInlineImageClick={handleInlineImageClick}
                  order={order}
                  selectedAreaID={selectedAreaID}
                  showTranslation={showTranslations}
                  translationToggle={translationToggle}
                />
              );
            }
            case 'VOICEMAIL':
              return (
                <ScrollView contentContainerStyle={styles.tabContentContainer}>
                  <OrderInboundsDetailVoicemailContent
                    isSummaryView={isSummaryView}
                    onSelect={onHighlightElementSelect}
                    order={props.route.orderMetadata}
                    selectedInputUUID={selectedAreaID}
                    showTranslation={showTranslations}
                    translationToggle={translationToggle}
                  />
                </ScrollView>
              );
            case 'MESSAGE':
              return (
                <ScrollView contentContainerStyle={styles.tabContentContainer}>
                  <OriginalInboundMessage
                    isSummaryView={isSummaryView}
                    messageHeader={translationToggle}
                    onSelect={onHighlightElementSelect}
                    order={props.route.orderMetadata}
                    selectedInputUUID={selectedAreaID}
                    showTranslation={showTranslations}
                  />
                </ScrollView>
              );
            case 'GROUPED_IMAGES':
              return (
                <ScrollView contentContainerStyle={styles.tabContentContainer}>
                  <OrderInboundsDetailGroupedImagesContent
                    images={props.route.images}
                    onAreaSelect={(input) => onAreaSelect?.(input)}
                    selectedAreaID={selectedAreaID}
                  />
                </ScrollView>
              );
            case 'GROUPED_HTML':
              return (
                <ScrollView contentContainerStyle={styles.tabContentContainer}>
                  <OrderInboundsDetailGroupedHtmlContent
                    attachments={props.route.attachments}
                    onHighlightElementSelect={(input) =>
                      onHighlightElementSelect?.(input)
                    }
                    order={props.route.orderMetadata}
                    selectedAreaID={selectedAreaID}
                  />
                </ScrollView>
              );
            case 'AUDIO_ATTACHMENT':
              return (
                <ScrollView contentContainerStyle={styles.tabContentContainer}>
                  <OriginalInboundAudio
                    attachmentIndex={props.route.attachmentIndex}
                    audioUrl={props.route.audioUrl}
                    messageHeader={translationToggle}
                    onSelect={onHighlightElementSelect}
                    orderUUID={props.route.orderMetadata.smartOrderUUID}
                    segments={props.route.orderMetadata.segments ?? []}
                    selectedInputUUID={selectedAreaID}
                    showTranslation={showTranslations}
                  />
                </ScrollView>
              );
            case 'EMAIL_THREAD':
              const handleEmlErrorForAttachment = (
                emlAttachment: InboundAttachment,
              ) => {
                handleEmlError(emlAttachment.url);
              };

              const handleHtmlHasNoContentForAttachment = (
                emlAttachment: InboundAttachment,
              ) => {
                handleHtmlHasNoContent(emlAttachment.url);
              };

              return (
                <View style={styles.emailThreadContainer}>
                  <OrderInboundsDetailEmailThreadContent
                    containerWidth={containerWidth}
                    items={props.route.items}
                    onAreaSelect={(input) => onAreaSelect?.(input)}
                    onAttachmentListItemPress={onEmlAttachmentListItemPress}
                    onEmlError={handleEmlErrorForAttachment}
                    onHighlightElementSelect={(input) =>
                      onHighlightElementSelect?.(input)
                    }
                    onHtmlHasNoContent={handleHtmlHasNoContentForAttachment}
                    onInlineImageClick={handleInlineImageClick}
                    selectedAreaID={selectedAreaID}
                    showTranslation={showTranslations}
                    translationToggle={translationToggle}
                  />

                  {!isSmallLayout &&
                  !isSmallTabletLayout &&
                  Boolean(pendingReplyThreadId) &&
                  pendingReplyThreadId === props.route.key ? (
                    <View
                      pointerEvents="box-none"
                      style={styles.toastContainer}
                    >
                      <Toast
                        onClose={() => setPendingReplyThreadId(null)}
                        options={{
                          containerStyle: styles.toastInnerContainer,
                          content: (
                            <View style={styles.toastContent}>
                              <AlertCircleIcon
                                fill={theme.sk.color.icon.warning.primary}
                                height={16}
                                width={16}
                              />

                              <Typography
                                color={theme.sk.color.text.warning}
                                variant="paragraph-md"
                              >
                                {i18n.t(
                                  'toast.email_thread.processing_new_reply',
                                )}
                              </Typography>
                            </View>
                          ),
                          showCloseButton: false,
                          type: Type.WARNING,
                        }}
                      />
                    </View>
                  ) : null}
                </View>
              );
            default:
              return null;
          }
        }}
        scrollEnabled
        style={containerStyle}
        tabBarTabStyle={styles.tabBarStyle}
      />
    </View>
  );
};

const TOAST_BOTTOM_OFFSET = 41;

const useStyles = createStyles((theme) => ({
  container: {
    backgroundColor: theme.sk.color.bg.primary.default,
    flex: 1,
  },
  emailThreadContainer: {
    flex: 1,
  },
  loadingContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  tabBarIndicatorStyle: {
    backgroundColor: theme.colors.accent,
  },
  tabBarStyle: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    width: 'auto',
  },
  tabContentContainer: {
    flexGrow: 1,
    padding: 24,
  },
  toastContainer: {
    bottom: TOAST_BOTTOM_OFFSET,
    left: 0,
    position: 'absolute',
    right: 0,
  },
  toastContent: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  toastInnerContainer: {
    backgroundColor: theme.sk.color.bg.warning,
    borderRadius: theme.sk.radii.radiusMedium,
    width: 343,
  },
}));
